// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { addToChainAfter, executeAwaitableHandlerChain, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import {
    chain,
    GetUnfilteredProducts,
    LoadRealTimeInventory,
    LoadRealTimePrices,
    ParseQueryParameter,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import { RealTimePricingModel } from "@insite/client-framework/Types/ApiModels";
import { CustomAppState } from "@tps/App/Store/Reducers";
import { xIsStockedItemsOnly } from "@tps/App/Store/Selectors/GeneralSelectors";
import qs from "qs";

export const TpsLoadRealTimePrices: any = async (props: any) => {
    // XNG-Change: TPS-626: depend on setting to either call realtime inventroy or not
    if (!(getSettingsCollection(props.getState()).catalogCustomSettings as any).enablePlpRealTimeCall) {
        return;
    }
    const {
        result: { productInfosByProductId },
    } = props;
    const productIds = Object.keys(productInfosByProductId);
    if (productIds.length === 0) {
        return;
    }

    const loadRealTimePricingParameter: Parameters<typeof loadRealTimePricing>[0] = {
        productPriceParameters: productIds.map(o => productInfosByProductId[o]!),
        onSuccess: realTimePricing => {
            props.dispatch({
                type: "Pages/ProductList/CompleteLoadRealTimePricing",
                realTimePricing,
            });
            props.pricingLoaded = true;
        },
        onError: () => {
            props.dispatch({
                type: "Pages/ProductList/FailedLoadRealTimePricing",
            });
            props.pricingLoaded = true;
        },
        onComplete(realTimePricingProps) {
            if (realTimePricingProps.apiResult) {
                this.onSuccess?.(realTimePricingProps.apiResult);
            } else if (realTimePricingProps.error) {
                this.onError?.(realTimePricingProps.error);
            }
        },
    };

    const settings = getSettingsCollection(props.getState());

    if (IS_SERVER_SIDE && getIsWebCrawler() && !settings.websiteSettings.includeRealTimeInventoryAndPricingForSeo) {
        const awaitableLoadRealTimePricing = makeHandlerChainAwaitable<
            Parameters<typeof loadRealTimePricing>[0],
            RealTimePricingModel
        >(loadRealTimePricing);
        const realTimePricing = await awaitableLoadRealTimePricing({
            productPriceParameters: loadRealTimePricingParameter.productPriceParameters,
        })(props.dispatch, props.getState);
        if (realTimePricing) {
            loadRealTimePricingParameter.onSuccess?.(realTimePricing);
        } else {
            loadRealTimePricingParameter.onError?.(realTimePricing);
        }
    } else {
        props.dispatch(loadRealTimePricing(loadRealTimePricingParameter));
    }

    const productSettings = settings.productSettings;
    if (productSettings.canSeePrices && productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => !!props.pricingLoaded);
    }
};

export const TpsLoadRealTimeInventory: any = (props: any) => {
    // XNG-Change: TPS-626: depend on setting to either call realtime inventroy or not
    if (!(getSettingsCollection(props.getState()).catalogCustomSettings as any).enablePlpRealTimeCall) {
        return;
    }
    if (
        !props.result.products?.length ||
        (getIsWebCrawler() &&
            !getSettingsCollection(props.getState()).websiteSettings.includeRealTimeInventoryAndPricingForSeo)
    ) {
        return;
    }

    props.dispatch(
        loadRealTimeInventory({
            productIds: props.result.products.map((o: any) => o.id),
            onSuccess: realTimeInventory => {
                props.dispatch({
                    type: "Pages/ProductList/CompleteLoadRealTimeInventory",
                    realTimeInventory,
                });
            },
            onError: () => {
                props.dispatch({
                    type: "Pages/ProductList/FailedLoadRealTimeInventory",
                });
            },
            onComplete(realTimeInventoryProps) {
                if (realTimeInventoryProps.apiResult) {
                    this.onSuccess?.(realTimeInventoryProps.apiResult);
                } else if (realTimeInventoryProps.error) {
                    this.onError?.(realTimeInventoryProps.error);
                }
            },
        }),
    );
};

export const XngParseQueryParameter: typeof ParseQueryParameter = props => {
    // XNG-Changes: TPS-1055 by enableSearchForStockedItemOnly setting check the stockedItemsOnly checkbox by default
    if (
        (getSettingsCollection(props.getState()).customSettings as any).enableSearchForStockedItemOnly &&
        xIsStockedItemsOnly(props.getState())
    ) {
        const { queryString } = props.parameter;
        const parsedQuery = qs.parse(queryString.startsWith("?") ? queryString.substr(1) : queryString);
        const { stockedItemsOnly } = parsedQuery;

        props.result.productFilters = {
            ...props.result.productFilters,
            stockedItemsOnly:
                stockedItemsOnly !== undefined ? stockedItemsOnly === "true" : xIsStockedItemsOnly(props.getState()),
        };
    }
};

// XNG-Changes
export const XngGetUnfilteredProducts: typeof GetUnfilteredProducts = async props => {
    // XNG-Changes - Check current attributeValueIds
    const currentAttributeValueIds = props.result.productFilters.attributeValueIds;
    const carbonStainlessFilter = (props.getState() as CustomAppState).xCarbonStainlessFilter?.selectedValue;

    const unfilteredApiParameter = { ...props.apiParameter };
    delete unfilteredApiParameter.searchWithin;
    if (!props.result.productFilters.pageBrandId) {
        delete unfilteredApiParameter.brandIds;
    }
    if (!props.result.productFilters.pageProductLineId) {
        delete unfilteredApiParameter.productLineIds;
    }
    delete unfilteredApiParameter.priceFilters;
    delete unfilteredApiParameter.attributeValueIds;

    if (!props.result.productFilters.pageCategoryId) {
        delete unfilteredApiParameter.categoryId;
    }
    unfilteredApiParameter.stockedItemsOnly = undefined;
    unfilteredApiParameter.previouslyPurchasedProducts = undefined;

    props.result.unfilteredApiParameter = unfilteredApiParameter;
    props.result.filteredApiParameter = { ...props.apiParameter };

    // XNG-Changes
    if (carbonStainlessFilter && currentAttributeValueIds?.includes(carbonStainlessFilter)) {
        unfilteredApiParameter.attributeValueIds = [carbonStainlessFilter];
    }

    const unfilteredProducts = getProductsDataView(props.getState(), unfilteredApiParameter).value;
    if (unfilteredProducts) {
        return;
    }
    await executeAwaitableHandlerChain(loadProducts, unfilteredApiParameter, props);
};

replaceInChain(chain, LoadRealTimePrices, TpsLoadRealTimePrices);
replaceInChain(chain, LoadRealTimeInventory, TpsLoadRealTimeInventory);
addToChainAfter(chain, ParseQueryParameter, XngParseQueryParameter);
replaceInChain(chain, GetUnfilteredProducts, XngGetUnfilteredProducts);
